/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
// eslint-disable-line import/named

import { OuiFormControlLayout, OuiValidatableControl } from '../form';
import { OuiErrorBoundary } from '../error_boundary';
import { OuiI18nConsumer } from '../context';
// @ts-ignore the type is provided by react-datepicker.d.ts
import { ReactDatePicker as _ReactDatePicker } from '../../../packages';
// eslint-disable-line import/no-unresolved

export var ouiDatePickerDefaultDateFormat = 'MM/DD/YYYY';
export var ouiDatePickerDefaultTimeFormat = 'hh:mm A';
var DatePicker = _ReactDatePicker;
export class OuiDatePicker extends Component {
  static defaultProps = {
    adjustDateOnChange: true,
    dateFormat: ouiDatePickerDefaultDateFormat,
    fullWidth: false,
    inputRef: () => {},
    isLoading: false,
    shadow: true,
    shouldCloseOnSelect: true,
    showMonthDropdown: true,
    showYearDropdown: true,
    yearDropdownItemNumber: 7,
    accessibleMode: true,
    showIcon: true,
    showTimeSelect: false,
    timeFormat: ouiDatePickerDefaultTimeFormat,
    popoverPlacement: 'bottom-start',
    compressed: false
  };
  render() {
    const {
      adjustDateOnChange,
      calendarClassName,
      className,
      customInput,
      dateFormat,
      dayClassName,
      disabled,
      excludeDates,
      filterDate,
      fullWidth,
      iconType,
      injectTimes,
      inline,
      inputRef,
      isInvalid,
      isLoading,
      locale,
      maxDate,
      maxTime,
      minDate,
      minTime,
      onChange,
      onClear,
      openToDate,
      placeholder,
      popperClassName,
      popoverPlacement,
      selected,
      shadow,
      shouldCloseOnSelect,
      showIcon,
      showTimeSelect,
      showTimeSelectOnly,
      timeFormat,
      utcOffset,
      compressed,
      ...rest
    } = this.props;
    const classes = classNames('ouiDatePicker', {
      'ouiDatePicker--shadow': shadow,
      'ouiDatePicker--inline': inline
    });
    const datePickerClasses = classNames('ouiDatePicker', 'ouiFieldText', {
      'ouiFieldText--fullWidth': fullWidth,
      'ouiFieldText-isLoading': isLoading,
      'ouiFieldText--withIcon': !inline && showIcon,
      'ouiFieldText-isInvalid': isInvalid,
      'ouiFieldText--compressed': compressed
    }, className);
    let optionalIcon;
    if (inline || customInput || !showIcon) {
      optionalIcon = undefined;
    } else if (iconType) {
      optionalIcon = iconType;
    } else if (showTimeSelectOnly) {
      optionalIcon = 'clock';
    } else {
      optionalIcon = 'calendar';
    }

    // In case the consumer did not alter the default date format but wants
    // to add the time select, we append the default time format
    let fullDateFormat = dateFormat;
    if (showTimeSelect && dateFormat === ouiDatePickerDefaultDateFormat) {
      fullDateFormat = `${dateFormat} ${timeFormat}`;
    }

    // OuiDatePicker only supports a subset of props from react-datepicker. Using any of
    // the unsupported props below will spit out an error.
    const PropNotSupported = () => {
      throw new Error(`You are using a prop from react-datepicker that OuiDatePicker
        does not support. Please check the OUI documentation for more information.`);
    };
    if (
    // @ts-ignore for guard against omitted prop
    // We don't want to show multiple months next to each other
    this.props.monthsShown ||
    // @ts-ignore for guard against omitted prop
    // There is no need to show week numbers
    this.props.showWeekNumbers ||
    // @ts-ignore for guard against omitted prop
    // Our css adapts to height, no need to fix it
    this.props.fixedHeight ||
    // @ts-ignore for guard against omitted prop
    // We force the month / year selection UI. No need to configure it
    this.props.dropdownMode ||
    // @ts-ignore for guard against omitted prop
    // Short month is unnecessary. Our UI has plenty of room for full months
    this.props.useShortMonthInDropdown ||
    // @ts-ignore for guard against omitted prop
    // The today button is not needed. This should always be external to the calendar
    this.props.todayButton ||
    // @ts-ignore for guard against omitted prop
    // We hide the time caption, so there is no need to overwrite its text
    this.props.timeCaption ||
    // @ts-ignore for guard against omitted prop
    // We always want keyboard accessibility on
    this.props.disabledKeyboardNavigation ||
    // @ts-ignore for guard against omitted prop
    // This is easy enough to do. It can conflict with isLoading state
    this.props.isClearable ||
    // @ts-ignore for guard against omitted prop
    // There is no reason to launch the datepicker in its own modal. Can always build these ourselves
    this.props.withPortal) {
      return <OuiErrorBoundary>
          <PropNotSupported />
        </OuiErrorBoundary>;
    }
    return <span>
        <span className={classes}>
          <OuiFormControlLayout icon={optionalIcon} fullWidth={fullWidth} clear={selected && onClear ? {
          onClick: onClear
        } : undefined} isLoading={isLoading}>
            <OuiValidatableControl isInvalid={isInvalid}>
              <OuiI18nConsumer>
                {({
                locale: contextLocale
              }) => {
                return <DatePicker adjustDateOnChange={adjustDateOnChange} calendarClassName={calendarClassName} className={datePickerClasses} customInput={customInput} dateFormat={fullDateFormat} dayClassName={dayClassName} disabled={disabled} excludeDates={excludeDates} filterDate={filterDate} injectTimes={injectTimes} inline={inline} locale={locale || contextLocale} maxDate={maxDate} maxTime={maxTime} minDate={minDate} minTime={minTime} onChange={onChange} openToDate={openToDate} placeholderText={placeholder} popperClassName={popperClassName} ref={inputRef} selected={selected} shouldCloseOnSelect={shouldCloseOnSelect} showMonthDropdown showTimeSelect={showTimeSelectOnly ? true : showTimeSelect} showTimeSelectOnly={showTimeSelectOnly} showYearDropdown timeFormat={timeFormat} utcOffset={utcOffset} yearDropdownItemNumber={7} accessibleMode popperPlacement={popoverPlacement} {...rest} />;
              }}
              </OuiI18nConsumer>
            </OuiValidatableControl>
          </OuiFormControlLayout>
        </span>
      </span>;
  }
}
OuiDatePicker.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  /**
     * Applies classes to the numbered days provided. Check docs for example.
     */
  dayClassName: PropTypes.func,
  /**
     * Makes the input full width
     */
  fullWidth: PropTypes.bool,
  /**
     * ref for the ReactDatePicker instance
     */
  inputRef: PropTypes.any.isRequired,
  /**
     * Provides styling to the input when invalid
     */
  isInvalid: PropTypes.bool,
  /**
     * Provides styling to the input when loading
     */
  isLoading: PropTypes.bool,
  /**
     * What to do when the input is cleared by the x icon
     */
  onClear: PropTypes.func,
  /**
     * Opens to this date (in moment format) on first press, regardless of selection
     */
  openToDate: PropTypes.any,
  /**
     * Shows only when no date is selected
     */
  placeholder: PropTypes.string,
  /**
     * Can turn the shadow off if using the inline prop
     */
  shadow: PropTypes.bool,
  /**
     * Show the icon in input
     */
  showIcon: PropTypes.bool,
  /**
     * Pass an icon type to change the default `calendar` or `clock` icon
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired, PropTypes.shape({
    type: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,
    side: PropTypes.any,
    ref: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired])
  }).isRequired]),
  /**
     * Sets the placement of the popover. It accepts: `"bottom"`, `"bottom-end"`, `"bottom-start"`, `"left"`, `"left-end"`, `"left-start"`, `"right"`, `"right-end"`, `"right-start"`, `"top"`, `"top-end"`, `"top-start"`
     */
  popoverPlacement: PropTypes.any,
  /**
     * Reduces the size to a typical (compressed) input
     */
  compressed: PropTypes.bool
};
try {
  OuiDatePicker.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/date_picker/date_picker.tsx',
    description: '',
    displayName: 'OuiDatePicker',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: 'Added to the actual input of the calendar',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      title: {
        defaultValue: null,
        description: '',
        name: 'title',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      autoFocus: {
        defaultValue: null,
        description: '',
        name: 'autoFocus',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      placeholder: {
        defaultValue: null,
        description: 'Shows only when no date is selected',
        name: 'placeholder',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      tabIndex: {
        defaultValue: null,
        description: '',
        name: 'tabIndex',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement, Element>) => void'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement, Element>) => void'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'What to do when the input changes',
        name: 'onChange',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      onKeyDown: {
        defaultValue: null,
        description: '',
        name: 'onKeyDown',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: KeyboardEvent<HTMLDivElement>) => void'
        }
      },
      onSelect: {
        defaultValue: null,
        description: '',
        name: 'onSelect',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      inline: {
        defaultValue: null,
        description: '',
        name: 'inline',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Pass an icon type to change the default `calendar` or `clock` icon',
        name: 'iconType',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'IconType | IconShape'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      isLoading: {
        defaultValue: {
          value: 'false'
        },
        description: 'Provides styling to the input when loading',
        name: 'isLoading',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: 'Makes the input full width',
        name: 'fullWidth',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: 'Provides styling to the input when invalid',
        name: 'isInvalid',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      locale: {
        defaultValue: null,
        description: 'Switches the locale / display. "en-us", "zn-ch"...etc',
        name: 'locale',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'LocaleSpecifier'
        }
      },
      compressed: {
        defaultValue: {
          value: 'false'
        },
        description: 'Reduces the size to a typical (compressed) input',
        name: 'compressed',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      readOnly: {
        defaultValue: null,
        description: '',
        name: 'readOnly',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      autoComplete: {
        defaultValue: null,
        description: '',
        name: 'autoComplete',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      required: {
        defaultValue: null,
        description: '',
        name: 'required',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      inputRef: {
        defaultValue: {
          value: '() => {}'
        },
        description: 'ref for the ReactDatePicker instance',
        name: 'inputRef',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Ref<Component<ReactDatePickerProps, any, any>>'
        }
      },
      onClear: {
        defaultValue: null,
        description: 'What to do when the input is cleared by the x icon',
        name: 'onClear',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      dayClassName: {
        defaultValue: null,
        description: 'Applies classes to the numbered days provided. Check docs for example.',
        name: 'dayClassName',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => string'
        }
      },
      openToDate: {
        defaultValue: null,
        description: 'Opens to this date (in moment format) on first press, regardless of selection',
        name: 'openToDate',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      shadow: {
        defaultValue: {
          value: 'true'
        },
        description: 'Can turn the shadow off if using the inline prop',
        name: 'shadow',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showIcon: {
        defaultValue: {
          value: 'true'
        },
        description: 'Show the icon in input',
        name: 'showIcon',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      popoverPlacement: {
        defaultValue: {
          value: 'bottom-start'
        },
        description: 'Sets the placement of the popover. It accepts: `"bottom"`, `"bottom-end"`, `"bottom-start"`, `"left"`, `"left-end"`, `"left-start"`, `"right"`, `"right-end"`, `"right-start"`, `"top"`, `"top-end"`, `"top-start"`',
        name: 'popoverPlacement',
        parent: {
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/date_picker.tsx',
          name: 'OuiExtendedDatePickerProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'popperPlacement',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }, {
            value: '"bottom-start"'
          }, {
            value: '"bottom-end"'
          }, {
            value: '"left-end"'
          }, {
            value: '"left-start"'
          }, {
            value: '"right-end"'
          }, {
            value: '"right-start"'
          }, {
            value: '"top-end"'
          }, {
            value: '"top-start"'
          }]
        }
      },
      adjustDateOnChange: {
        defaultValue: {
          value: 'true'
        },
        description: 'Whether changes to Year and Month (via dropdowns) should trigger `onChange`',
        name: 'adjustDateOnChange',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      accessibleMode: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'accessibleMode',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      allowSameDay: {
        defaultValue: null,
        description: '',
        name: 'allowSameDay',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      calendarClassName: {
        defaultValue: null,
        description: 'Optional class added to the calendar portion of datepicker',
        name: 'calendarClassName',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      customInput: {
        defaultValue: null,
        description: 'Replaces the input with any node, like a button',
        name: 'customInput',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      customInputRef: {
        defaultValue: null,
        description: '',
        name: 'customInputRef',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      dateFormat: {
        defaultValue: {
          value: 'MM/DD/YYYY'
        },
        description: 'Accepts any moment format string',
        name: 'dateFormat',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string | string[]'
        }
      },
      dateFormatCalendar: {
        defaultValue: null,
        description: '',
        name: 'dateFormatCalendar',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      endDate: {
        defaultValue: null,
        description: '',
        name: 'endDate',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      excludeDates: {
        defaultValue: null,
        description: '',
        name: 'excludeDates',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      excludeTimes: {
        defaultValue: null,
        description: '',
        name: 'excludeTimes',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      filterDate: {
        defaultValue: null,
        description: '',
        name: 'filterDate',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => boolean'
        }
      },
      forceShowMonthNavigation: {
        defaultValue: null,
        description: '',
        name: 'forceShowMonthNavigation',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      formatWeekNumber: {
        defaultValue: null,
        description: '',
        name: 'formatWeekNumber',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => string | number'
        }
      },
      highlightDates: {
        defaultValue: null,
        description: '',
        name: 'highlightDates',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      includeDates: {
        defaultValue: null,
        description: '',
        name: 'includeDates',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      includeTimes: {
        defaultValue: null,
        description: '',
        name: 'includeTimes',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      injectTimes: {
        defaultValue: null,
        description: 'Adds additional times to the time selector other then :30 increments',
        name: 'injectTimes',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      maxDate: {
        defaultValue: null,
        description: 'The max date accepted (in moment format) as a selection',
        name: 'maxDate',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      maxTime: {
        defaultValue: null,
        description: 'The max time accepted (in moment format) as a selection',
        name: 'maxTime',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      minDate: {
        defaultValue: null,
        description: 'The min date accepted (in moment format) as a selection',
        name: 'minDate',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      minTime: {
        defaultValue: null,
        description: 'The min time accepted (in moment format) as a selection',
        name: 'minTime',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      onChangeRaw: {
        defaultValue: null,
        description: '',
        name: 'onChangeRaw',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement, Element>) => void'
        }
      },
      onClickOutside: {
        defaultValue: null,
        description: '',
        name: 'onClickOutside',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(event: MouseEvent<HTMLDivElement, MouseEvent>) => void'
        }
      },
      onMonthChange: {
        defaultValue: null,
        description: '',
        name: 'onMonthChange',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => void'
        }
      },
      onWeekSelect: {
        defaultValue: null,
        description: '',
        name: 'onWeekSelect',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(firstDayOfWeek: Moment, weekNumber: string | number, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      onYearChange: {
        defaultValue: null,
        description: '',
        name: 'onYearChange',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(date: Moment) => void'
        }
      },
      peekNextMonth: {
        defaultValue: null,
        description: '',
        name: 'peekNextMonth',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      placeholderText: {
        defaultValue: null,
        description: '',
        name: 'placeholderText',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      popperClassName: {
        defaultValue: null,
        description: 'Class applied to the popup, when inline is false',
        name: 'popperClassName',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      popperContainer: {
        defaultValue: null,
        description: '',
        name: 'popperContainer',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: '(props: { children: ReactNode[]; }) => ReactNode'
        }
      },
      popperPlacement: {
        defaultValue: null,
        description: '',
        name: 'popperPlacement',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'popperPlacement',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }, {
            value: '"bottom-start"'
          }, {
            value: '"bottom-end"'
          }, {
            value: '"left-end"'
          }, {
            value: '"left-start"'
          }, {
            value: '"right-end"'
          }, {
            value: '"right-start"'
          }, {
            value: '"top-end"'
          }, {
            value: '"top-start"'
          }]
        }
      },
      preventOpenOnFocus: {
        defaultValue: null,
        description: '',
        name: 'preventOpenOnFocus',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      scrollableMonthYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'scrollableMonthYearDropdown',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      scrollableYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'scrollableYearDropdown',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      selected: {
        defaultValue: null,
        description: 'The selected datetime (in moment format)',
        name: 'selected',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      selectsEnd: {
        defaultValue: null,
        description: '',
        name: 'selectsEnd',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      selectsStart: {
        defaultValue: null,
        description: '',
        name: 'selectsStart',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      shouldCloseOnSelect: {
        defaultValue: {
          value: 'true'
        },
        description: 'Will close the popup on selection',
        name: 'shouldCloseOnSelect',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showDisabledMonthNavigation: {
        defaultValue: null,
        description: '',
        name: 'showDisabledMonthNavigation',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showMonthDropdown: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'showMonthDropdown',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showMonthYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'showMonthYearDropdown',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showTimeSelect: {
        defaultValue: {
          value: 'false'
        },
        description: 'Show the time selection alongside the calendar',
        name: 'showTimeSelect',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showTimeSelectOnly: {
        defaultValue: null,
        description: 'Only show the time selector, not the calendar',
        name: 'showTimeSelectOnly',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showYearDropdown: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'showYearDropdown',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      startDate: {
        defaultValue: null,
        description: '',
        name: 'startDate',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'Moment'
        }
      },
      startOpen: {
        defaultValue: null,
        description: '',
        name: 'startOpen',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      strictParsing: {
        defaultValue: null,
        description: 'Use Moment strict mode, allowing exact format matches only',
        name: 'strictParsing',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      timeFormat: {
        defaultValue: {
          value: 'hh:mm A'
        },
        description: 'The format of the time within the selector, in moment notation',
        name: 'timeFormat',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      timeIntervals: {
        defaultValue: null,
        description: '',
        name: 'timeIntervals',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      useWeekdaysShort: {
        defaultValue: null,
        description: '',
        name: 'useWeekdaysShort',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      utcOffset: {
        defaultValue: null,
        description: '',
        name: 'utcOffset',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      weekLabel: {
        defaultValue: null,
        description: '',
        name: 'weekLabel',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      yearDropdownItemNumber: {
        defaultValue: {
          value: '7'
        },
        description: '',
        name: 'yearDropdownItemNumber',
        parent: {
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        declarations: [{
          fileName: 'src/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'ReactDatePickerProps', 'OuiExtendedDatePickerProps']
  };
} catch (e) {}